import React, { createContext, useContext, useMemo } from 'react';
import { serverTimestamp } from 'firebase/firestore';
import { setDoc } from './writes';
import { docRef, COLLECTIONS } from './paths';
import { useAuth } from '../context/AuthContext';
import { freeId } from '../domain/freeId';
import {
  listedTemplates, packageTemplateDocument, templateRemovalDocument,
} from '../domain/packageTemplates';
import { useCollectionWindow } from './useCollectionWindow';

// Package templates — the managed list a package is sold from (#181).
//
// The rules a template obeys are `src/domain/packageTemplates.js`; this file is
// where the documents are shaped and written. Same division as
// `useNoteTemplates.jsx`, and the same simpler half of the categories pattern:
// there is no built-in template, so an empty collection is an empty collection
// and none of `useCategories`' first-write machinery has anything to guard.
//
// Random ids. Two devices offline, each adding `5 Hour Block`, add two
// templates; that is `domain/freeId.js`'s "genuinely separate" case. Nothing
// points at a template — a sale copies its fields (see the domain header) — so
// a duplicate is a duplicate row in a list and not a double charge.
//
// ── Delete is soft, because MMS says it is ────────────────────────────────
//
// MMS's confirm dialog: *"If this package template has been used, it will be
// marked as 'Inactive'."* So `removeTemplate` writes `status: 'inactive'` and
// `deleted: true` rather than calling `deleteDoc`.
//
// **Ours is unconditional where theirs is conditional.** MMS hard-deletes a
// template nothing was sold from; doing that here would mean a client asking
// "has anything ever been sold from this?" of a collection it windows to 5,000
// documents, and getting a wrong answer offline (the shape of #70). A row
// nobody sold from, marked inactive, is invisible on every surface either way.

const PackageTemplatesContext = createContext(null);

// The same bound the other studio-level managed lists use; the sale wizard's
// dropdown is a flat menu.
const TRACK_LIMIT = 200;

export function PackageTemplatesProvider({ children }) {
  const { user } = useAuth();

  // The templates window, from whichever read path serves this session
  // (#207 U5). No `withMetadata`: nothing here turns on a pending write.
  const read = useCollectionWindow({
    uid: user?.uid,
    collection: COLLECTIONS.packageTemplates,
    limit: TRACK_LIMIT,
  });
  const stored = read.docs;

  const value = useMemo(() => {
    // Sorted by name, as every other managed list here is; MMS's order is not
    // recorded.
    const listed = listedTemplates(stored);

    return {
      loaded: read.hasRead,
      // The read stopped before it answered (#647) — src/domain/reading.js.
      failed: read.failed,
      // Everything stored, tombstones included — for a reader that needs to
      // resolve a name on an old sale. Nothing reads it, because a sale copies
      // the name; it is exposed so that the filter below is visibly a *display*
      // rule rather than the only access.
      all: stored,
      templates: listed,
      byId: (id) => stored.find((t) => t.id === id) || null,

      // Create *and* edit: passing an id writes that template in place.
      //
      // Editing a template moves nothing on a sale already made, as in MMS —
      // a rename, a reprice or a re-duration — so there is no fan-out here.
      saveTemplate: async ({
        id, name, description, hours, minutes, priceMinor, status, expires, expiryDays,
      }) => {
        if (!user) return null;
        const tid = id || freeId('pkgtpl');
        // `packageTemplateDocument` (src/domain/packageTemplates.js): one
        // `minutes` field, not two — the form has two boxes and MMS normalises
        // between them on blur, because the stored thing is a total; the price
        // never negative, since MMS strips the minus at the keystroke; and
        // `expiryDays` null rather than zero when the toggle is off, so "no
        // expiry" and "expires in zero days" cannot be confused. Null for a
        // duration that is not positive, which writes nothing.
        const data = packageTemplateDocument({
          name, description, hours, minutes, priceMinor, status, expires, expiryDays,
        }, serverTimestamp());
        if (!data) return null;
        await setDoc(docRef(user.uid, COLLECTIONS.packageTemplates, tid), data, { merge: true });
        return tid;
      },

      // Soft — see the header. Both fields are written because they answer
      // different questions: `status` is the one a teacher set and can set
      // back, `deleted` is the one this wrote.
      removeTemplate: async (id) => {
        if (!user || !id) return;
        await setDoc(
          docRef(user.uid, COLLECTIONS.packageTemplates, id),
          templateRemovalDocument(serverTimestamp()),
          { merge: true },
        );
      },
    };
  }, [stored, read.hasRead, read.failed, user]);

  return (
    <PackageTemplatesContext.Provider value={value}>{children}</PackageTemplatesContext.Provider>
  );
}

export function usePackageTemplates() {
  const ctx = useContext(PackageTemplatesContext);
  if (!ctx) throw new Error('usePackageTemplates must be used within a PackageTemplatesProvider');
  return ctx;
}
